import React, { Fragment } from 'react';
import { cn } from '@v2/shared/lib/utils';
import { Category } from '@v2/shared/lib/types';
import Container from '@v2/shared/components/Container';
import { Disclosure, Transition } from '@headlessui/react';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Logo from '@v2/shared/Layouts/Full/Navigation/Logo';
import Search from '@v2/shared/Layouts/Full/Navigation/Search';
import CategoryMenu from '@v2/shared/Layouts/Full/Navigation/CategoryMenu';
import MobileCategories from '@v2/shared/Layouts/Full/Navigation/MobileCategories';

import logo from './images/logo.svg';
import CartButton from './CartButton';
import AccountItems from './AccountItems';

type Props = {
  categories?: Category[];
};

/*
 * The header runs to the edges below md.
 *
 * Tailwind derives `container`'s max-widths from `screens`, and xs (375px) and sm (414px)
 * are phone widths -- so between 414px and 768px the container stops growing and the
 * header sits in a 414px column with the page showing down either side. That is fine for
 * a content column on a wide screen and wrong for a header, which should reach the edges
 * until there is a reason not to.
 *
 * max-w-none undoes it, and the breakpoint variants put back exactly what `container`
 * would have set from md up -- they have to be restated because max-w-none is
 * unconditional and would otherwise win at every width. Utilities are emitted after the
 * container's own rules and in breakpoint order, so each one wins where it applies.
 */
const HEADER_WIDTH = 'max-w-none md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl';

/**
 * headless: apps/topmags/layouts/MainLayout/Navigation.
 *
 * In normal flow rather than fixed, which is the structural difference from
 * DiscountMags' header and the reason this brand needs no height measurement or spacer.
 *
 * The strip is `md:bg-primary`, and primary is themed: it resolves to --v2-primary,
 * which styles/v2.css sets to TopMags' red under [data-brand='topmags']. The class is
 * the same one DiscountMags renders; only the value differs.
 *
 * The mark, the search box and both category lists come from
 * `shared/Layouts/Full/Navigation`, which is where this brand's copies of them went when
 * MagSpot and MagZoom arrived needing the same four. What is left here is what TopMags
 * says for itself: its own logo file, its account chips and its shopping bag.
 */
export default function Navigation({ categories }: Props) {
  return (
    <nav>
      <Disclosure>
        {/* pt-4 only below md: p-1 left 4px above the logo against 12px and a rule
            below it, which read as top-heavy once the page behind it went white. md
            keeps its own pt-6. */}
        <Container className={cn(HEADER_WIDTH, 'bg-white p-1 pt-4', 'md:px-5 md:pb-4 md:pt-6 md:bg-transparent')}>
          <div className="flex justify-between items-center md:items-end">
            <Disclosure.Button className="text-slate-400 text-sm md:hidden px-1.5">
              <FontAwesomeIcon icon={faBars} size="2x" />
            </Disclosure.Button>
            <Logo src={logo} width={192} height={44} className="w-[155px] h-[35px] md:w-[192px] md:h-[44px]" />
            <span className="flex-col items-end hidden md:flex">
              <AccountItems />
            </span>
            {/* mr-0.5 brings the bag's right edge to 10px from the container, where
                the hamburger's glyph and the search bar below both sit. CartButton's own
                mr-1 leaves it at 8px, and it is not the place to fix that -- AccountItems
                renders the same component in the desktop header and the mobile menu,
                neither of which is lining up with anything here. */}
            <span className="md:hidden mr-0.5">
              <CartButton />
            </span>
          </div>
        </Container>

        {/* The rule under the search sits out here rather than on the Container so it
            reaches both edges of the screen. The Container is centred and takes a
            max-width from the xs/sm breakpoints, so on a phone it is a little narrower
            than the viewport -- a border drawn on it stops short of each edge, which
            reads as the header being boxed. Nothing else in this header shows that,
            because everything else is white on a white page. */}
        <div className="border-b border-neutral-300 md:border-0 md:bg-primary md:mb-5">
          {/* px-2.5 below md so the bar lines up with the row above it. That row is
              p-1, and its hamburger adds px-1.5 of its own, which puts the glyph 10px in
              from the container edge; the cart's mr-1 leaves it 8px from the other. The
              Container's default px-5 left the search 20px in and visibly narrower than
              the icons bracketing it. md keeps px-5, where the bar is 2/3 width and
              aligns with nothing. */}
          <Container className={cn(HEADER_WIDTH, 'bg-white md:bg-transparent px-2.5 pt-2 pb-3 md:px-5 md:py-0')}>
            {/* The bar's `md:py-2` is on this row rather than on the coloured wrapper, so
                that the row's bottom edge is the bar's bottom edge — which is what the
                category panel hangs off. The painted height is unchanged either way: the
                wrapper takes its height from what is inside it. */}
            <div className="flex flex-col sm:flex-row items-center justify-between relative md:py-2">
              <div className="w-full sm:w-auto mb-2 sm:mb-0 text-center sm:text-left sm:mr-5 hidden md:block">
                <CategoryMenu categories={categories} />
              </div>
              <div className="w-full md:w-2/3 pb-1 sm:pb-0">
                <Search id="topmags-search" />
              </div>
            </div>

            <div className="overflow-hidden">
              <Transition
                appear
                as={Fragment}
                enter="transition duration-200 ease-out"
                enterFrom="transform -translate-y-full opacity-0"
                enterTo="transform translate-y-0 opacity-100"
                leave="transition duration-175 ease-out"
                leaveFrom="transform translate-y-0 opacity-100"
                leaveTo="transform -translate-y-full opacity-0"
              >
                <Disclosure.Panel className="md:hidden flex flex-col items-center justify-center">
                  <AccountItems />
                  <MobileCategories categories={categories} />
                </Disclosure.Panel>
              </Transition>
            </div>
          </Container>
        </div>
      </Disclosure>
    </nav>
  );
}
